import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, IconButton, Avatar, Menu, MenuItem, Divider, Tooltip, Badge, Typography, List, ListItem, ListItemText, Paper, ClickAwayListener, Chip, useTheme } from '@mui/material';
import {
  Search as SearchIcon,
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  AdminPanelSettings as AdminPanelIcon,
  DoneAll as DoneAllIcon,
  DeleteSweep as ClearAllIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  Subject as SubjectIcon,
  CreditCard as BillingIcon,
  ManageAccounts as ManageAccountIcon,
  Settings as SettingsMenuIcon,
  Logout as LogoutIcon,
  Bookmark as BookmarkIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../../stores/authStore';
import { useMailStore } from '../../stores/mailStore';
import { useNotificationStore } from '../../stores/notificationStore';
import { getInitials } from '../../utils/helpers';

// Avatar color palette — deterministic from email address
const AVATAR_COLORS = ['#1a73e8','#e91e63','#9c27b0','#ff5722','#4caf50','#ff9800','#00bcd4','#f06292','#607d8b','#795548'];
const getAvatarColor = (str: string) => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};
import { useBranding } from '../../utils/BrandingContext';
import api from '../../utils/api';

// ─── Search token types & operator definitions ──────────────────────────────
interface SearchToken { op: string; value: string; label?: string; }

const SEARCH_OPERATORS = [
  { op: 'from',        label: 'from:',                   desc: 'from email address or name' },
  { op: 'to',          label: 'to:',                     desc: 'to email address' },
  { op: 'to/cc',       label: 'to/cc:',                  desc: 'to or cc email address contains' },
  { op: 'subject',     label: 'subject:',                desc: 'subject contains' },
  { op: 'contains',    label: 'contains:',               desc: 'text in email content, attachments and headers' },
  { op: 'body',        label: 'email content contains:', desc: 'text in core email content' },
  { op: 'date',        label: 'date:',                   desc: 'emails on this date (YYYY-MM-DD)' },
  { op: 'after',       label: 'from date:',              desc: 'emails after this date' },
  { op: 'before',      label: 'to date:',                desc: 'emails before this date' },
  { op: 'filename',    label: 'attachment name:',        desc: 'attachment name contains' },
  { op: 'has',         label: 'with attachment:',        desc: 'filter by email with attachment', preset: 'attachment' },
  { op: 'is',          label: 'unread:',                 desc: 'filter by unread emails',         preset: 'unread' },
  { op: 'label',       label: 'tag:',                    desc: 'has the tag / label' },
  { op: '-from',       label: 'from does not contain:',  desc: 'from email address should not contain' },
  { op: '-to',         label: 'to/cc does not contain:', desc: 'to / cc does not contain' },
] as const;

const RECENT_SEARCHES_KEY = 'cruvz_recent_searches';
const MAX_RECENT = 5;

interface HybridHeaderProps {
  onMenuClick?: () => void;
  isMobile?: boolean;
}

const HybridHeader: React.FC<HybridHeaderProps> = ({ onMenuClick, isMobile = false }) => {
  const navigate = useNavigate();
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === 'dark';
  const { user, logout } = useAuthStore();
  const { emails, setSearchQuery, searchQuery: activeSearchQuery, setSearchScope, setSearchSort, currentFolder, savedSearches, createSavedSearch, fetchSavedSearches } = useMailStore();
  const { notifications, unreadCount, markAsRead, markAllAsRead, clearAll } = useNotificationStore();
  const { branding } = useBranding();

  // ── Token-based search state ──────────────────────────────────────────────
  const [tokens, setTokens]           = useState<SearchToken[]>([]);
  const [activeOp, setActiveOp]       = useState<string | null>(null); // operator badge being built (e.g. 'from')
  const [inputText, setInputText]     = useState('');                  // free text or value after activeOp
  const [searchFocused, setSearchFocused] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY) || '[]'); } catch { return []; }
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const liveSearchRef = useRef(false); // suppress live-search on first render

  // Contact autocomplete from API
  const [contactResults, setContactResults] = useState<{ addr: string; name: string }[]>([]);
  const contactTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Derive known senders from loaded emails
  const knownSenders = useMemo(() => {
    const seen = new Map<string, string>();
    emails.forEach((e: any) => {
      const addr = typeof e.from === 'string' ? e.from : (e.from?.address || '');
      const name = typeof e.from === 'string' ? '' : (e.from?.name || '');
      if (addr && !seen.has(addr)) seen.set(addr, name);
    });
    return Array.from(seen.entries()).map(([addr, name]) => ({ addr, name }));
  }, [emails]);

  // Fetch contacts from API when typing a from/to value
  useEffect(() => {
    const isContactOp = activeOp === 'from' || activeOp === 'to' || activeOp === 'to/cc' || activeOp === '-from' || activeOp === '-to';
    if (!isContactOp) { setContactResults([]); return; }
    if (contactTimer.current) clearTimeout(contactTimer.current);
    contactTimer.current = setTimeout(async () => {
      try {
        const res = await api.get('/contacts/autocomplete', { params: { q: inputText || ' ' } });
        setContactResults((res.data || []).map((c: any) => ({
          addr: c.email || c.address || '',
          name: c.name || c.displayName || '',
        })).filter((c: { addr: string }) => c.addr));
      } catch { setContactResults([]); }
    }, 280);
    return () => { if (contactTimer.current) clearTimeout(contactTimer.current); };
  }, [activeOp, inputText]);

  // Load saved searches on mount
  useEffect(() => { fetchSavedSearches(); }, []);

  // ── Build query string from tokens + freeText ─────────────────────────────
  const buildQuery = useCallback((toks: SearchToken[], op: string | null, text: string) => {
    const parts = toks.map(t => `${t.op}:${t.value}`);
    if (op && text.trim()) parts.push(`${op}:${text.trim()}`);
    else if (!op && text.trim()) parts.push(text.trim());
    return parts.join(' ');
  }, []);

  // ── Live search — fires 600ms after any token/text change ─────────────────
  useEffect(() => {
    if (!liveSearchRef.current) { liveSearchRef.current = true; return; }
    const q = buildQuery(tokens, activeOp, inputText);
    const timer = setTimeout(() => {
      if (q.trim()) {
        setSearchQuery(q);
        if (!window.location.pathname.includes('/mail')) navigate('/mail/inbox');
      } else if (activeSearchQuery) {
        setSearchQuery('');
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [tokens, inputText]); // intentionally minimal deps for debounce

  // ── Operator + contact suggestions ────────────────────────────────────────
  const suggestions = useMemo(() => {
    const text = inputText.trim().toLowerCase();

    // Active operator: show contact/value suggestions
    if (activeOp) {
      const isContactOp = ['from', 'to', 'to/cc', '-from', '-to'].includes(activeOp);
      const isSubjectOp = activeOp === 'subject' || activeOp === 'contains' || activeOp === 'body';

      if (isContactOp) {
        const merged = new Map<string, string>();
        knownSenders.forEach(s => merged.set(s.addr.toLowerCase(), s.name));
        contactResults.forEach(c => { if (!merged.has(c.addr.toLowerCase())) merged.set(c.addr.toLowerCase(), c.name); });
        const filtered = Array.from(merged.entries())
          .filter(([addr, name]) => !text || addr.includes(text) || name.toLowerCase().includes(text))
          .slice(0, 12)
          .map(([addr, name]) => ({ label: name || addr, value: addr, secondary: name ? addr : '' }));
        return { type: 'contacts' as const, items: filtered, emails: [], operators: [], recent: [] };
      }

      if (isSubjectOp) {
        const seen = new Set<string>();
        const items: { label: string; value: string; secondary: string }[] = [];
        emails.forEach((e: any) => {
          const s = (e.subject || '').trim();
          if (s && !seen.has(s) && (!text || s.toLowerCase().includes(text))) {
            seen.add(s);
            const fromName = typeof e.from === 'string' ? e.from : (e.from?.name || e.from?.address || '');
            items.push({ label: s, value: s, secondary: `from ${fromName}` });
          }
        });
        return { type: 'subjects' as const, items: items.slice(0, 8), emails: [], operators: [], recent: [] };
      }

      return { type: 'freetext' as const, items: [], emails: [], operators: [], recent: [] };
    }

    // No active operator, no text → show operator list + saved searches + recent
    if (!text) {
      return { type: 'empty' as const, items: [], emails: [], operators: SEARCH_OPERATORS, recent: recentSearches };
    }

    // Free text typed → show matched emails + operator hints + recent
    const matchedEmails = emails.filter((e: any) => {
      const sub = (e.subject || '').toLowerCase();
      const fromAddr = typeof e.from === 'string' ? e.from.toLowerCase() : (e.from?.address || '').toLowerCase();
      const fromName = typeof e.from === 'string' ? '' : (e.from?.name || '').toLowerCase();
      return sub.includes(text) || fromAddr.includes(text) || fromName.includes(text);
    }).slice(0, 5);
    const matchedRecent = recentSearches.filter(r => r.toLowerCase().includes(text)).slice(0, 3);
    return { type: 'freetext' as const, items: [], emails: matchedEmails, operators: [], recent: matchedRecent };
  }, [activeOp, inputText, knownSenders, contactResults, emails, recentSearches]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const saveRecentSearch = (q: string) => {
    const updated = [q, ...recentSearches.filter(r => r !== q)].slice(0, MAX_RECENT);
    setRecentSearches(updated);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
  };

  const executeSearch = useCallback((q: string) => {
    if (!q.trim()) return;
    saveRecentSearch(q.trim());
    setSearchQuery(q.trim());
    setShowSuggestions(false);
    setSearchFocused(false);
    navigate('/mail/inbox');
    inputRef.current?.blur();
  }, [recentSearches]);

  const commitToken = useCallback((value: string, label?: string) => {
    if (!activeOp || !value.trim()) return;
    setTokens(prev => [...prev, { op: activeOp, value: value.trim(), label }]);
    setActiveOp(null);
    setInputText('');
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [activeOp]);

  const removeToken = useCallback((i: number) => {
    setTokens(prev => prev.filter((_, idx) => idx !== i));
    setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  const handleClear = useCallback(() => {
    setTokens([]);
    setActiveOp(null);
    setInputText('');
    setSearchQuery('');
    setSearchScope('all');
    setSearchSort('date');
    setContactResults([]);
    setShowSuggestions(false);
    liveSearchRef.current = false;
    setTimeout(() => { liveSearchRef.current = true; }, 0);
    inputRef.current?.focus();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    // Detect if user completed typing an operator (e.g. "from:")
    if (!activeOp) {
      const lower = val.toLowerCase().trimStart();
      for (const opDef of SEARCH_OPERATORS) {
        // Match exact operator label (e.g. "from:", "email content contains:")
        if (lower === opDef.label || lower.endsWith(' ' + opDef.label)) {
          if ('preset' in opDef && opDef.preset) {
            // Instant-value operator (has:attachment, is:unread)
            setTokens(prev => [...prev, { op: opDef.op, value: opDef.preset as string }]);
          } else {
            setActiveOp(opDef.op);
          }
          setInputText('');
          setShowSuggestions(true);
          return;
        }
      }
    }
    setInputText(val);
    setShowSuggestions(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (activeOp && inputText.trim()) { commitToken(inputText.trim()); e.preventDefault(); return; }
      const q = buildQuery(tokens, activeOp, inputText);
      if (q.trim()) executeSearch(q);
      return;
    }
    if (e.key === 'Tab' && activeOp && inputText.trim()) { commitToken(inputText.trim()); e.preventDefault(); return; }
    if (e.key === 'Backspace' && !inputText) {
      if (activeOp) { setActiveOp(null); return; }
      if (tokens.length > 0) { setTokens(prev => prev.slice(0, -1)); }
      return;
    }
    if (e.key === 'Escape') { handleClear(); inputRef.current?.blur(); setSearchFocused(false); }
  };

  const handleOperatorSelect = (opDef: typeof SEARCH_OPERATORS[number]) => {
    if ('preset' in opDef && opDef.preset) {
      setTokens(prev => [...prev, { op: opDef.op, value: opDef.preset as string }]);
      setInputText('');
    } else {
      setActiveOp(opDef.op);
      setInputText('');
    }
    setShowSuggestions(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleContactSelect = (item: { label: string; value: string }) => {
    commitToken(item.value, item.label);
    setShowSuggestions(true);
  };

  const hasContent = tokens.length > 0 || !!activeOp || !!inputText;
  const showDropdown = showSuggestions && searchFocused;

  // ── Notifications + user menu state ──────────────────────────────────────
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifAnchor, setNotifAnchor] = useState<null | HTMLElement>(null);

  const handleLogout = () => {
    setUserMenuAnchor(null);
    logout();
    navigate('/login');
  };

  return (
    <Box
      className="header"
      sx={{
        background: muiTheme.palette.background.paper,
        borderBottom: `1px solid ${muiTheme.palette.divider}`,
        height: isMobile ? '56px' : '64px',
        display: 'flex',
        alignItems: 'center',
        padding: isMobile ? '0 12px' : '0 20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        position: 'sticky',
        top: 0,
        zIndex: 1100,
      }}
    >
      {/* Menu toggle button */}
      {onMenuClick && (
        <IconButton
          onClick={onMenuClick}
          sx={{
            width: isMobile ? '40px' : '36px',
            height: isMobile ? '40px' : '36px',
            borderRadius: '8px',
            mr: isMobile ? 1 : 2,
            '&:hover': { background: '#f3f4f6' },
          }}
        >
          <MenuIcon fontSize={isMobile ? 'medium' : 'small'} />
        </IconButton>
      )}

      {/* Logo Section */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          flexShrink: 0,
        }}
        onClick={() => navigate('/mail/inbox')}
      >
        {branding.logo ? (
          /* Custom uploaded logo */
          <Box
            component="img"
            src={branding.logo}
            alt={branding.companyName || 'Logo'}
            sx={{
              height: isMobile ? '32px' : '56px',
              width: 'auto',
              maxWidth: isMobile ? '112px' : '208px',
              display: 'block',
              objectFit: 'contain',
            }}
          />
        ) : branding.companyName && branding.companyName !== 'CruvzMail' ? (
          /* Custom domain without logo — gradient text fallback */
          <Box
            sx={{
              fontSize: '1.5rem',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '-0.3px',
            }}
          >
            {branding.companyName}
          </Box>
        ) : (
          /* Default CruvzMail logo */
          <Box
            component="img"
            src="/logo-hd.png"
            alt="CruvzMail"
            sx={{
              height: isMobile ? '40px' : '70px',
              width: 'auto',
              maxWidth: isMobile ? '140px' : '260px',
              display: 'block',
              objectFit: 'contain',
            }}
          />
        )}
      </Box>

      {/* Spacer */}
      <Box sx={{ flexGrow: 1 }} />

      {/* Search Bar - Hide on small mobile, show as icon */}
      {!isMobile ? (
        <ClickAwayListener onClickAway={() => { setShowSuggestions(false); setSearchFocused(false); }}>
          <Box
            sx={{
              flexGrow: searchFocused ? 1 : 0,
              width: searchFocused ? '100%' : '420px',
              maxWidth: '680px',
              mr: 2,
              transition: 'all 0.2s ease',
              position: 'relative',
            }}
          >
            {/* ── Tokenized search input container ── */}
            <Box
              onClick={() => { inputRef.current?.focus(); setShowSuggestions(true); setSearchFocused(true); }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'nowrap',
                gap: 0.5,
                height: '40px',
                overflowX: 'auto',
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none',
                px: 1.5,
                py: 0.5,
                bgcolor: searchFocused ? '#fff' : '#f1f3f4',
                borderRadius: '24px',
                border: `1px solid ${searchFocused ? '#c5cae9' : 'transparent'}`,
                boxShadow: searchFocused ? '0 1px 3px rgba(0,0,0,0.2), 0 4px 8px rgba(0,0,0,0.06)' : 'none',
                cursor: 'text',
                transition: 'all 0.2s ease',
                '&:hover': { bgcolor: searchFocused ? '#fff' : '#e8eaed' },
              }}
            >
              <SearchIcon
                sx={{ fontSize: 20, color: searchFocused ? '#1a73e8' : '#9ca3af', flexShrink: 0, mr: 0.5 }}
                onClick={(e) => { e.stopPropagation(); const q = buildQuery(tokens, activeOp, inputText); if (q.trim()) executeSearch(q); }}
              />

              {/* Completed token chips */}
              {tokens.map((token, i) => (
                <Box key={i} sx={{
                  display: 'inline-flex', alignItems: 'center', bgcolor: '#e8f0fe',
                  borderRadius: '4px', px: 0.75, py: 0.25, gap: 0.5, flexShrink: 0,
                }}>
                  <Typography sx={{ fontSize: '0.78rem', color: '#1a73e8', fontWeight: 700, lineHeight: 1 }}>
                    {token.op}:
                  </Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: '#1f2937', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1 }}>
                    {token.label || token.value}
                  </Typography>
                  <IconButton size="small"
                    onClick={(e) => { e.stopPropagation(); removeToken(i); }}
                    sx={{ p: 0, width: 14, height: 14, '& svg': { fontSize: 11 } }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              ))}

              {/* Active operator badge (e.g. "from:") */}
              {activeOp && (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', bgcolor: '#1a73e8', borderRadius: '4px', px: 0.75, py: 0.25, flexShrink: 0 }}>
                  <Typography sx={{ fontSize: '0.78rem', color: '#fff', fontWeight: 700, lineHeight: 1 }}>{activeOp}:</Typography>
                </Box>
              )}

              {/* Native input */}
              <input
                ref={inputRef}
                value={inputText}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => { setSearchFocused(true); setShowSuggestions(true); }}
                placeholder={
                  tokens.length === 0 && !activeOp
                    ? 'Search mail, contacts, or type from: to: subject:'
                    : activeOp
                    ? `Type ${activeOp} value…`
                    : ''
                }
                style={{
                  flex: 1,
                  minWidth: '120px',
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  fontSize: '0.875rem',
                  fontFamily: 'inherit',
                  color: 'inherit',
                  paddingTop: '2px',
                  paddingBottom: '2px',
                }}
              />

              {/* Clear all button */}
              {hasContent && (
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); handleClear(); }}
                  sx={{ p: 0.25, flexShrink: 0, ml: 0.5, color: '#9ca3af', '&:hover': { color: '#4a5568' } }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Box>

            {/* ── Suggestions Dropdown ── */}
            {showDropdown && (
              <Paper
                elevation={4}
                sx={{
                  position: 'absolute',
                  top: 'calc(100% + 4px)',
                  left: 0,
                  right: 0,
                  borderRadius: '12px',
                  border: '1px solid #e0e4e8',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.13)',
                  zIndex: 1500,
                  overflow: 'hidden',
                  maxHeight: 460,
                  overflowY: 'auto',
                }}
              >
                {/* ─ Active operator: contact/subject suggestions ─ */}
                {activeOp && (suggestions.type === 'contacts' || suggestions.type === 'subjects') && (
                  <Box>
                    <Typography sx={{ px: 2, pt: 1.5, pb: 0.5, fontSize: '0.7rem', color: '#80868b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {suggestions.type === 'contacts' ? `${activeOp} suggestions` : 'subject suggestions'}
                    </Typography>
                    {suggestions.items.length === 0 ? (
                      <Typography sx={{ px: 2, py: 1, fontSize: '0.85rem', color: '#9aa0a6' }}>
                        Type to search {activeOp === 'from' || activeOp === 'to' || activeOp === 'to/cc' ? 'contacts' : 'subjects'}…
                      </Typography>
                    ) : suggestions.items.map((item) => (
                      <ListItem key={item.value}
                        onClick={() => handleContactSelect(item)}
                        sx={{ px: 2, py: 0.75, cursor: 'pointer', '&:hover': { bgcolor: '#f1f3f4' }, gap: 1.5, alignItems: 'center' }}
                      >
                        {suggestions.type === 'contacts' ? (
                          <Avatar sx={{ width: 30, height: 30, fontSize: '0.68rem', bgcolor: getAvatarColor(item.value), flexShrink: 0 }}>
                            {getInitials(item.label)}
                          </Avatar>
                        ) : (
                          <SubjectIcon sx={{ fontSize: 18, color: '#9aa0a6', flexShrink: 0 }} />
                        )}
                        <Box sx={{ minWidth: 0 }}>
                          <Typography noWrap sx={{ fontSize: '0.875rem', color: '#202124', lineHeight: 1.3 }}>{item.label}</Typography>
                          {item.secondary && item.secondary !== item.label && (
                            <Typography noWrap sx={{ fontSize: '0.75rem', color: '#80868b' }}>{item.secondary}</Typography>
                          )}
                        </Box>
                      </ListItem>
                    ))}
                    {/* Hint: press Enter/Tab to commit */}
                    {inputText && (
                      <Box sx={{ px: 2, py: 1, borderTop: '1px solid #f1f3f4', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SearchIcon sx={{ fontSize: 15, color: '#1a73e8' }} />
                        <Typography sx={{ fontSize: '0.82rem', color: '#5f6368' }}>
                          Press <strong>Enter</strong> to search <span style={{ color: '#1a73e8' }}>{activeOp}:{inputText}</span>
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}

                {/* ─ No active operator, empty input → operator list ─ */}
                {suggestions.type === 'empty' && (
                  <>
                    {/* Saved searches */}
                    {savedSearches.length > 0 && (
                      <Box sx={{ borderBottom: '1px solid #f1f3f4' }}>
                        <Typography sx={{ px: 2, pt: 1.5, pb: 0.5, fontSize: '0.7rem', color: '#80868b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Saved Searches</Typography>
                        {savedSearches.slice(0, 4).map((s: any) => (
                          <ListItem key={s.id || s._id} onClick={() => executeSearch(s.query || s.name)}
                            sx={{ px: 2, py: 0.75, cursor: 'pointer', '&:hover': { bgcolor: '#f1f3f4' }, gap: 1.5 }}
                          >
                            <BookmarkIcon sx={{ fontSize: 15, color: '#1a73e8', flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '0.875rem', color: '#3c4043' }}>{s.name || s.query}</Typography>
                          </ListItem>
                        ))}
                      </Box>
                    )}

                    {/* Recent searches */}
                    {recentSearches.length > 0 && (
                      <Box sx={{ borderBottom: '1px solid #f1f3f4' }}>
                        <Typography sx={{ px: 2, pt: 1.5, pb: 0.5, fontSize: '0.7rem', color: '#80868b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Recent</Typography>
                        {recentSearches.map((r) => (
                          <ListItem key={r} onClick={() => executeSearch(r)}
                            sx={{ px: 2, py: 0.75, cursor: 'pointer', '&:hover': { bgcolor: '#f1f3f4' }, gap: 1.5 }}
                          >
                            <HistoryIcon sx={{ fontSize: 16, color: '#9aa0a6', flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '0.875rem', color: '#3c4043' }}>{r}</Typography>
                          </ListItem>
                        ))}
                      </Box>
                    )}

                    {/* 14+ operators list */}
                    <Box>
                      <Typography sx={{ px: 2, pt: 1.5, pb: 0.5, fontSize: '0.7rem', color: '#80868b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Filter by</Typography>
                      {SEARCH_OPERATORS.map((opDef) => (
                        <ListItem key={opDef.op}
                          onClick={() => handleOperatorSelect(opDef)}
                          sx={{ px: 2, py: 0.6, cursor: 'pointer', '&:hover': { bgcolor: '#f1f3f4' }, display: 'flex', gap: 2, alignItems: 'flex-start' }}
                        >
                          <Typography sx={{ fontSize: '0.85rem', color: '#1a73e8', fontWeight: 600, minWidth: 170, flexShrink: 0 }}>{opDef.label}</Typography>
                          <Typography sx={{ fontSize: '0.82rem', color: '#5f6368' }}>{opDef.desc}</Typography>
                        </ListItem>
                      ))}
                    </Box>
                  </>
                )}

                {/* ─ Free text typed → matched emails + recent + operator hints ─ */}
                {suggestions.type === 'freetext' && (
                  <>
                    {/* Matched recent */}
                    {suggestions.recent.length > 0 && (
                      <Box sx={{ borderBottom: '1px solid #f1f3f4' }}>
                        {suggestions.recent.map((r) => (
                          <ListItem key={r} onClick={() => executeSearch(r)}
                            sx={{ px: 2, py: 0.75, cursor: 'pointer', '&:hover': { bgcolor: '#f1f3f4' }, gap: 1.5 }}
                          >
                            <HistoryIcon sx={{ fontSize: 16, color: '#9aa0a6', flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '0.875rem', color: '#3c4043' }}>{r}</Typography>
                          </ListItem>
                        ))}
                      </Box>
                    )}

                    {/* Matched emails */}
                    {suggestions.emails.length > 0 && (
                      <Box sx={{ borderBottom: '1px solid #f1f3f4' }}>
                        <Typography sx={{ px: 2, pt: 1.5, pb: 0.5, fontSize: '0.7rem', color: '#80868b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Emails</Typography>
                        {suggestions.emails.map((e: any) => {
                          const fromAddr = typeof e.from === 'string' ? e.from : (e.from?.address || '');
                          const fromName = typeof e.from === 'string' ? '' : (e.from?.name || '');
                          const displayName = fromName || fromAddr;
                          const dateStr = e.date ? new Date(e.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
                          return (
                            <ListItem key={e.id} onClick={() => executeSearch(e.subject || fromAddr)}
                              sx={{ px: 2, py: 0.75, cursor: 'pointer', '&:hover': { bgcolor: '#f1f3f4' }, gap: 1.5, alignItems: 'flex-start' }}
                            >
                              <Avatar sx={{ width: 28, height: 28, mt: 0.25, fontSize: '0.65rem', bgcolor: getAvatarColor(fromAddr), flexShrink: 0 }}>
                                {getInitials(displayName)}
                              </Avatar>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography noWrap sx={{ fontSize: '0.85rem', fontWeight: e.isRead ? 400 : 600, color: '#202124' }}>{displayName}</Typography>
                                  <Typography sx={{ fontSize: '0.72rem', color: '#9aa0a6', flexShrink: 0, ml: 1 }}>{dateStr}</Typography>
                                </Box>
                                <Typography noWrap sx={{ fontSize: '0.78rem', color: '#5f6368' }}>{e.subject}</Typography>
                              </Box>
                            </ListItem>
                          );
                        })}
                      </Box>
                    )}

                    {/* Operator hints */}
                    {inputText.trim() && (
                      <Box sx={{ borderBottom: '1px solid #f1f3f4' }}>
                        {(['from', 'to', 'subject', 'contains'] as const).map((op) => (
                          <ListItem key={op}
                            onClick={() => { setActiveOp(op); setInputText(''); setShowSuggestions(true); setTimeout(() => inputRef.current?.focus(), 0); }}
                            sx={{ px: 2, py: 0.6, cursor: 'pointer', '&:hover': { bgcolor: '#f1f3f4' }, gap: 1.5 }}
                          >
                            <SearchIcon sx={{ fontSize: 15, color: '#9aa0a6', flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '0.82rem', color: '#5f6368' }}>
                              <span style={{ color: '#1a73e8', fontWeight: 600 }}>{op}:</span> {inputText}
                            </Typography>
                          </ListItem>
                        ))}
                      </Box>
                    )}

                    {/* Search for "X" footer */}
                    {inputText.trim() && (
                      <ListItem onClick={() => executeSearch(buildQuery(tokens, activeOp, inputText))}
                        sx={{ px: 2, py: 0.9, cursor: 'pointer', '&:hover': { bgcolor: '#f1f3f4' }, gap: 1.5 }}
                      >
                        <SearchIcon sx={{ fontSize: 16, color: '#1a73e8', flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.875rem', color: '#1a73e8', fontWeight: 500 }}>
                          Search for "{inputText}"
                        </Typography>
                      </ListItem>
                    )}
                  </>
                )}
              </Paper>
            )}
          </Box>
        </ClickAwayListener>
      ) : (
        /* Mobile search icon */
        <Tooltip title="Search" arrow>
          <IconButton
            onClick={() => {
              // TODO: Open search dialog/page for mobile
              console.log('Mobile search clicked');
            }}
            sx={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              mr: 0.5,
              color: '#4a5568',
              '&:hover': { background: '#f3f4f6' },
            }}
          >
            <SearchIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {/* Action Icons */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0.5 : 0.25, mr: isMobile ? '50px' : '60px' }}>

        {/* Notifications */}
        <Tooltip title="Notifications" arrow>
          <IconButton
            onClick={(e) => { setNotifAnchor(e.currentTarget); setNotifOpen(true); }}
            sx={{
              width: isMobile ? '40px' : '32px',
              height: isMobile ? '40px' : '32px',
              borderRadius: '8px',
              color: '#4a5568',
              '&:hover': { background: '#f3f4f6' },
            }}
          >
            <Badge badgeContent={unreadCount > 0 ? unreadCount : null} color="error" max={99}
              sx={{ '& .MuiBadge-badge': { fontSize: '10px', height: 16, minWidth: 16, top: 2, right: 2 } }}>
              <NotificationsIcon sx={{ fontSize: isMobile ? 24 : 22 }} />
            </Badge>
          </IconButton>
        </Tooltip>

        {/* Notification Dropdown */}
        {notifOpen && (
          <ClickAwayListener onClickAway={() => setNotifOpen(false)}>
            <Paper
              elevation={4}
              sx={{
                position: 'fixed',
                top: isMobile ? '56px' : '60px',
                right: isMobile ? '8px' : '80px',
                width: isMobile ? 'calc(100vw - 16px)' : 360,
                maxWidth: 360,
                maxHeight: isMobile ? 'calc(100vh - 64px)' : 480,
                borderRadius: '12px',
                border: '1px solid #e0e4e8',
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                zIndex: 1400,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              {/* Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: '1px solid #e8eaed' }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#1f2937' }}>
                  Notifications {unreadCount > 0 && <Box component="span" sx={{ ml: 0.75, px: 0.75, py: 0.1, bgcolor: '#ef4444', color: '#fff', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 700 }}>{unreadCount}</Box>}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {unreadCount > 0 && (
                    <Tooltip title="Mark all read" arrow>
                      <IconButton size="small" onClick={markAllAsRead} sx={{ color: '#1a73e8', p: 0.5 }}>
                        <DoneAllIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {notifications.length > 0 && (
                    <Tooltip title="Clear all" arrow>
                      <IconButton size="small" onClick={clearAll} sx={{ color: '#5f6368', p: 0.5 }}>
                        <ClearAllIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>

              {/* List */}
              <Box sx={{ overflowY: 'auto', flex: 1 }}>
                {notifications.length === 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 5, color: '#9ca3af' }}>
                    <NotificationsIcon sx={{ fontSize: 40, mb: 1, opacity: 0.4 }} />
                    <Typography sx={{ fontSize: '0.875rem' }}>No notifications</Typography>
                  </Box>
                ) : (
                  <List disablePadding>
                    {notifications.map((n, idx) => (
                      <React.Fragment key={n.id}>
                        <ListItem
                          onClick={() => {
                            markAsRead(n.id);
                            if (n.data?.emailId && n.data?.folderId) {
                              navigate(`/mail/${n.data.folderId}`);
                              setNotifOpen(false);
                            }
                          }}
                          sx={{
                            px: 2, py: 1.25,
                            cursor: n.data?.emailId ? 'pointer' : 'default',
                            bgcolor: n.read ? 'transparent' : '#f0f7ff',
                            '&:hover': { bgcolor: '#f3f4f6' },
                            alignItems: 'flex-start',
                            gap: 1,
                          }}
                        >
                          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: n.read ? 'transparent' : '#1a73e8', mt: 0.75, flexShrink: 0 }} />
                          <ListItemText
                            primary={<Typography sx={{ fontSize: '0.85rem', fontWeight: n.read ? 400 : 600, color: '#1f2937', lineHeight: 1.4 }}>{n.title}</Typography>}
                            secondary={
                              <Box>
                                <Typography sx={{ fontSize: '0.78rem', color: '#6b7280', mt: 0.25 }}>{n.message}</Typography>
                                <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', mt: 0.5 }}>
                                  {new Date(n.timestamp).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                        {idx < notifications.length - 1 && <Divider sx={{ mx: 2 }} />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Box>
            </Paper>
          </ClickAwayListener>
        )}

        {/* Admin Panel Icon - only for admin/superadmin */}
        {(user?.role === 'admin' || user?.role === 'superadmin') && (
          <Tooltip title="Admin Panel" arrow>
            <IconButton
              onClick={() => {
                // Share token to localStorage so admin panel (same origin) reads it directly.
                // H-06: refresh token is in the httpOnly cookie (same origin) — no need to hand it off.
                const token = localStorage.getItem('accessToken');
                if (token) localStorage.setItem('token', token);
                window.open('/admin/dashboard', '_blank');
              }}
              sx={{
                width: isMobile ? '40px' : '36px',
                height: isMobile ? '40px' : '36px',
                borderRadius: '8px',
                color: '#7c3aed',
                '&:hover': { background: '#f5f3ff' },
              }}
            >
              <AdminPanelIcon sx={{ fontSize: isMobile ? 26 : 24 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* User Avatar - Positioned at far right corner */}
      <Avatar
        src={user?.avatar || undefined}
        onClick={(e) => setUserMenuAnchor(e.currentTarget)}
        sx={{
          width: isMobile ? '36px' : '38px',
          height: isMobile ? '36px' : '38px',
          background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
          color: '#fff',
          fontWeight: 700,
          fontSize: isMobile ? '0.85rem' : '0.9rem',
          cursor: 'pointer',
          ml: isMobile ? 1 : 2,
          mr: 0,
          border: '2px solid #fff',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          position: 'absolute',
          right: isMobile ? '5px' : '5px',
          '&:hover': {
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.2)',
          },
        }}
      >
        {!user?.avatar && getInitials(user?.email || 'U')}
      </Avatar>

      {/* User Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={() => setUserMenuAnchor(null)}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 240,
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            border: '1px solid #e0e4e8',
          },
        }}
      >
        {/* User info header */}
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            src={user?.avatar || undefined}
            sx={{
              width: 40,
              height: 40,
              background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
              fontWeight: 700,
              fontSize: '0.85rem',
            }}
          >
            {!user?.avatar && getInitials(user?.email || 'U')}
          </Avatar>
          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
              {user?.firstName} {user?.lastName}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
              {user?.email}
            </Typography>
          </Box>
        </Box>
        <Divider />
        <MenuItem onClick={() => { setUserMenuAnchor(null); navigate('/profile'); }} sx={{ py: 1, gap: 1.5 }}>
          <PersonIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          Profile
        </MenuItem>
        <MenuItem onClick={() => { setUserMenuAnchor(null); navigate('/billing'); }} sx={{ py: 1, gap: 1.5 }}>
          <BillingIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          Plan & Billing
        </MenuItem>
        <MenuItem onClick={() => { setUserMenuAnchor(null); navigate('/settings/preset'); }} sx={{ py: 1, gap: 1.5 }}>
          <ManageAccountIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          Manage Account
        </MenuItem>
        {(user?.role === 'admin' || user?.role === 'superadmin') && (
          <MenuItem
            onClick={() => {
              setUserMenuAnchor(null);
              // H-06: refresh token is in the httpOnly cookie (same origin) — no need to hand it off.
              const token = localStorage.getItem('accessToken');
              if (token) localStorage.setItem('token', token);
              window.open('/admin/dashboard', '_blank');
            }}
            sx={{ py: 1, gap: 1.5, color: '#7c3aed' }}
          >
            <AdminPanelIcon sx={{ fontSize: 18 }} />
            Admin Panel
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => { setUserMenuAnchor(null); navigate('/settings'); }} sx={{ py: 1, gap: 1.5 }}>
          <SettingsMenuIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          Settings
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ py: 1, gap: 1.5, color: 'error.main' }}>
          <LogoutIcon sx={{ fontSize: 18 }} />
          Logout
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default HybridHeader;
